import React, { useState, useEffect, useCallback } from 'react';
import GlassCard from '../ui/GlassCard';
import GlassButton from '../ui/GlassButton';
import { formIntakeApi, FormIntakeForm, FormIntakeStyleDef } from '../../services/api';

const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  return fallback;
};

const COLOR_FIELDS: Array<{ key: keyof FormIntakeStyleDef; label: string; fallback: string }> = [
  { key: 'header_color', label: 'Header Color', fallback: '#0F172A' },
  { key: 'text_color', label: 'Text Color', fallback: '#0F172A' },
  { key: 'background_color', label: 'Card Background', fallback: '#FFFFFF' },
  { key: 'page_background', label: 'Page Background', fallback: '#FFFFFF' },
  { key: 'input_background', label: 'Input Background', fallback: '#FFFFFF' },
  { key: 'input_text_color', label: 'Input Text Color', fallback: '#0F172A' },
  { key: 'border_color', label: 'Border Color', fallback: '#E2E8F0' },
  { key: 'button_color', label: 'Button Color', fallback: '#2177FF' },
  { key: 'button_text_color', label: 'Button Text Color', fallback: '#FFFFFF' },
];

interface FormStyleTabProps {
  form: FormIntakeForm;
  onSaved: (form: FormIntakeForm) => void;
  projectId?: string;
}

const FormStyleTab: React.FC<FormStyleTabProps> = ({ form, onSaved, projectId }) => {
  const [style, setStyle] = useState<FormIntakeStyleDef>(form.style ?? {});
  const [brandDefault, setBrandDefault] = useState<FormIntakeStyleDef | null>(null);
  const [savedDefault, setSavedDefault] = useState<FormIntakeStyleDef | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setStyle(form.style ?? {});
    setError('');
    setSuccess('');
  }, [form.form_id]);

  const loadDefaults = useCallback(async () => {
    try {
      const res = await formIntakeApi.getStyleDefaults(form.form_id);
      setBrandDefault(res.brand);
      setSavedDefault(res.saved_default);
    } catch {
      // Non-fatal — the tab still works without the reset buttons' source data.
    }
  }, [form.form_id]);

  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  const updateField = (key: keyof FormIntakeStyleDef, value: string) => {
    setStyle((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const saveStyle = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Presentation-only write — works whether the form is draft or published, unlike the
      // Editor tab's schema save which 409s once published.
      const updated = await formIntakeApi.updatePresentation(form.form_id, { style });
      setSuccess('Style saved.');
      onSaved(updated);
    } catch (err: any) {
      setError(getApiError(err, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const saveAsProjectDefault = async () => {
    if (!projectId) return;
    setSavingPreset(true);
    setError('');
    setSuccess('');
    try {
      // Deliberately a SEPARATE write from saveStyle above — this does not save the form's own
      // style, only the project-level preset new forms will be seeded from going forward.
      await formIntakeApi.saveProjectStyleDefault(projectId, style);
      setSuccess('Saved as your project default — new forms will start with these colours.');
      setSavedDefault(style);
    } catch (err: any) {
      setError(getApiError(err, 'Could not save project default.'));
    } finally {
      setSavingPreset(false);
    }
  };

  const previewVars: React.CSSProperties = {
    ...(style.header_color ? { '--pff-header': style.header_color } as any : {}),
    ...(style.text_color ? { '--pff-text': style.text_color } as any : {}),
    ...(style.background_color ? { '--pff-bg': style.background_color } as any : {}),
    ...(style.input_background ? { '--pff-input-bg': style.input_background } as any : {}),
    ...(style.input_text_color ? { '--pff-input-text': style.input_text_color } as any : {}),
    ...(style.border_color ? { '--pff-border': style.border_color } as any : {}),
    ...(style.button_color ? { '--pff-btn-bg': style.button_color } as any : {}),
    ...(style.button_text_color ? { '--pff-btn-text': style.button_text_color } as any : {}),
    ...(style.font_family ? { fontFamily: `'${style.font_family}', -apple-system, sans-serif` } : {}),
  };

  return (
    <div className="fi-editor">
      <GlassCard>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Fill Page Colours</h3>
        <div className="color-inputs">
          {COLOR_FIELDS.map(({ key, label, fallback }) => (
            <div className="form-group" key={key}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                {label}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="color"
                  value={(style[key] as string) || fallback}
                  onChange={(e) => updateField(key, e.target.value)}
                  style={{
                    width: '48px', height: '48px', border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)', background: 'transparent', cursor: 'pointer',
                  }}
                />
                <span style={{ fontSize: '14px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                  {(style[key] as string) || 'unset'}
                </span>
                {style[key] && (
                  <button
                    type="button"
                    onClick={() => updateField(key, '')}
                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            Font Family
          </label>
          <input
            type="text"
            className="pff-input"
            value={style.font_family || ''}
            onChange={(e) => updateField('font_family', e.target.value)}
            placeholder="e.g. Georgia (falls back to the visitor's system font if not installed)"
            style={{ maxWidth: '400px' }}
          />
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
            Names a font family — doesn't load one. If the visitor's device doesn't have it installed, the page falls back to its default font stack.
          </p>
        </div>
      </GlassCard>

      <GlassCard style={{ marginTop: '16px' }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Preview</h4>
        <div className="pff-style-preview" style={previewVars}>
          <div className="pff-style-preview-card">
            <div className="pff-style-preview-title">Sample Form</div>
            <div className="pff-style-preview-field">
              <label>Full name</label>
              <input type="text" readOnly value="Jane Smith" />
            </div>
            <button type="button" className="pff-style-preview-btn">Submit</button>
          </div>
        </div>
      </GlassCard>

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: '16px', flexWrap: 'wrap' }}>
        {brandDefault && (
          <GlassButton variant="secondary" onClick={() => setStyle(brandDefault)}>
            Reset to Brand Colours
          </GlassButton>
        )}
        {savedDefault && (
          <GlassButton variant="secondary" onClick={() => setStyle(savedDefault)}>
            Reset to My Default
          </GlassButton>
        )}
        {projectId && (
          <GlassButton variant="secondary" onClick={saveAsProjectDefault} disabled={savingPreset}>
            {savingPreset ? 'Saving…' : 'Save as Project Default'}
          </GlassButton>
        )}
        <GlassButton variant="primary" onClick={saveStyle} disabled={saving}>
          {saving ? 'Saving…' : 'Save Style'}
        </GlassButton>
      </div>

      {error && (
        <div className="fi-error" style={{ marginTop: '12px' }}>{error}</div>
      )}
      {success && (
        <div style={{ color: 'var(--success, #16a34a)', fontSize: 'var(--text-sm)', marginTop: '12px' }}>
          {success}
        </div>
      )}
    </div>
  );
};

export default FormStyleTab;
